import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import type {
  Frame,
  HandInput,
  KernelEvent,
  LoadResult,
  LocalHost,
  ModeDefinition,
  RemoteInput,
  ReplayExport,
  RunBundle,
  RuntimeConfig,
} from "@picnic-field/local-host/protocol";
import { LatencyProbe } from "../../remote";
import { courseDeps } from "../lib/deps";
import { initialRun, runReducer } from "../lib/flow";
import { splitGhosts } from "../lib/ghosts";
import { courseAxis, followPos, progressOf, remainingMeters, surfaceAt } from "../lib/progress";
import type { HostStatus, Hud, RunState, GhostLoad, CourseAxis } from "../types";

/**
 * FS-04 한 판 — 커널 호스트(C-05) 수명 · 준비(load) · 출발 · 프레임/이벤트 → 상태(flow) · 리플레이 내보내기 · 지연 계측.
 * 프레임은 60Hz 로 오지만 React 상태는 표시 값이 바뀔 때만(HUD) 갱신한다 — 3D 뷰는 ref 로 바로 받는다.
 */
export interface CourseRun {
  status: HostStatus;
  load: LoadResult | null;
  run: RunState;
  hud: Hud;
  /** 끝난 판의 리플레이(커널 ReplayFile v1 — S4). 기록이 없으면 null */
  exportReplay(): Promise<ReplayExport | null>;
  prepare(presetKey: string, ghosts: readonly GhostLoad[]): Promise<void>;
  start(): void;
  pause(): void;
  resume(): void;
  reset(): void;
  giveUp(): void;
  input(s: RemoteInput): void;
  /** 기구 무대 '센서 손'(허브 기울기 · 힘 · 색 · 거리) — 호스트가 모르면 무시 */
  hand(h: HandInput): void;
  /** 3D 뷰 등 프레임 구독(60Hz) */
  onFrame(cb: (f: Frame) => void): () => void;
  latestFrame(): Frame | null;
  probe: LatencyProbe;
  host(): LocalHost | null;
}

const EMPTY_HUD: Hud = { clockS: 0, countdown: false, progress: 0, remainingM: null };

export function useCourseRun(opts: {
  bundle: RunBundle | null;
  mode: ModeDefinition | null;
  config: RuntimeConfig | null;
  /** S5 교사 '현실감'(SR-RUN-06) — LoadRequest.realism */
  realism?: boolean;
  onEvent?: (e: KernelEvent) => void;
  /** 고른 고스트를 커널이 받지 못해 고스트 없이 준비했다 */
  onGhostError?: () => void;
}): CourseRun {
  const { bundle, mode, config } = opts;
  const realism = opts.realism === true;
  const [status, setStatus] = useState<HostStatus>("creating");
  const [load, setLoad] = useState<LoadResult | null>(null);
  const [run, dispatch] = useReducer(runReducer, undefined, initialRun);
  const [hud, setHud] = useState<Hud>(EMPTY_HUD);

  const hostRef = useRef<LocalHost | null>(null);
  const frameCbs = useRef(new Set<(f: Frame) => void>());
  const latest = useRef<Frame | null>(null);
  const axis = useRef<CourseAxis | null>(null);
  const loadRef = useRef<LoadResult | null>(null);
  const phaseRef = useRef(run.phase);
  phaseRef.current = run.phase;
  const onEventRef = useRef(opts.onEvent);
  onEventRef.current = opts.onEvent;
  const onGhostErrorRef = useRef(opts.onGhostError);
  onGhostErrorRef.current = opts.onGhostError;
  const probe = useRef(new LatencyProbe()).current;
  const loadSeq = useRef(0);
  const hudKey = useRef("");

  // 호스트 만들기(화면당 하나) · 해제
  useEffect(() => {
    let alive = true;
    let host: LocalHost | null = null;
    const unsubs: (() => void)[] = [];
    courseDeps()
      .createHost()
      .then((h) => {
        if (!alive) return h.dispose();
        host = h;
        hostRef.current = h;
        unsubs.push(
          h.onFrame((f) => {
            latest.current = f;
            probe.markFrame(performance.now());
            const l = loadRef.current;
            if (l) {
              const p = followPos(f, l.followIndex);
              if (!axis.current) axis.current = courseAxis(l.bodies, f.transforms, p);
              const progress = axis.current ? progressOf(axis.current, p) : 0;
              const surface = surfaceAt(l.bodies, f.transforms, p);
              if (phaseRef.current === "driving") {
                dispatch({ type: "sample", progress, surface, timeMs: f.timeMs });
              }
              const countdown = f.remainingS != null;
              const next: Hud = {
                clockS: countdown ? (f.remainingS ?? 0) : f.timeMs / 1000,
                countdown,
                progress,
                remainingM: axis.current ? remainingMeters(axis.current, progress) : null,
              };
              // 표시가 바뀔 때만(0.1초 · 0.1m · 진행 1%)
              const key = `${Math.floor(next.clockS * 10)}|${Math.round((next.remainingM ?? 0) * 10)}|${Math.round(progress * 100)}`;
              if (key !== hudKey.current) {
                hudKey.current = key;
                setHud(next);
              }
            }
            frameCbs.current.forEach((cb) => cb(f));
          }),
          h.onEvent((e) => {
            dispatch({ type: "kernel", event: e });
            onEventRef.current?.(e);
          }),
        );
        setStatus("idle");
      })
      .catch(() => alive && setStatus("error"));
    return () => {
      alive = false;
      unsubs.forEach((u) => u());
      host?.dispose();
      hostRef.current = null;
    };
  }, [probe]);

  // 판이 끝나면 호스트를 멈춘다(마지막 장면은 그대로)
  useEffect(() => {
    if (run.phase === "over") hostRef.current?.pause();
  }, [run.phase]);

  const prepare = useCallback(
    async (presetKey: string, ghosts: readonly GhostLoad[]) => {
      const h = hostRef.current;
      if (!h || !bundle || !mode || !config) return;
      const seq = ++loadSeq.current;
      latest.current = null;
      setStatus("loading");
      try {
        const req = {
          me: bundle,
          mode,
          presetKey,
          runtimeConfig: config,
          seed: (Date.now() >>> 0) % 2 ** 31,
          record: true,
          realism,
        };
        let r: LoadResult;
        try {
          r = await h.load({ ...req, ...splitGhosts(ghosts, config.local.maxGhosts) });
        } catch (e) {
          // 읽을 수 없는 고스트(깨진 리플레이 등) 때문이면 고스트 없이 다시 — 혼자라도 달릴 수 있게
          if (!ghosts.length || seq !== loadSeq.current) throw e;
          r = await h.load(req);
          onGhostErrorRef.current?.();
        }
        if (seq !== loadSeq.current) return;
        loadRef.current = r;
        hudKey.current = "";
        dispatch({ type: "reset" });
        setLoad(r);
        // 첫 프레임(시작 전 모습)은 load 가 끝나기 전에 온다 — 거리·진행을 그 프레임으로 채운다
        const f0 = latest.current as Frame | null; // (await 사이에 바뀐다 — 좁히기 무시)
        axis.current = f0 ? courseAxis(r.bodies, f0.transforms, followPos(f0, r.followIndex)) : null;
        const p0 = f0 && axis.current ? progressOf(axis.current, followPos(f0, r.followIndex)) : 0;
        setHud({
          clockS: r.timerS,
          countdown: r.timerS > 0,
          progress: p0,
          remainingM: axis.current ? remainingMeters(axis.current, p0) : null,
        });
        setStatus("ready");
      } catch {
        if (seq === loadSeq.current) setStatus("error");
      }
    },
    [bundle, mode, config, realism],
  );

  return {
    status,
    load,
    run,
    hud,
    exportReplay: async () => {
      const h = hostRef.current;
      if (!h || typeof h.exportReplay !== "function") return null;
      return h.exportReplay().catch(() => null);
    },
    prepare,
    start: () => {
      hostRef.current?.start();
      dispatch({ type: "start" });
    },
    pause: () => {
      hostRef.current?.pause();
      dispatch({ type: "pause" });
    },
    resume: () => {
      hostRef.current?.resume();
      dispatch({ type: "resume" });
    },
    reset: () => hostRef.current?.reset(),
    giveUp: () => hostRef.current?.giveUp(),
    input: (s) => {
      probe.markInput(performance.now());
      hostRef.current?.input(s);
    },
    hand: (h) => hostRef.current?.hand?.(h),
    onFrame: (cb) => {
      frameCbs.current.add(cb);
      return () => frameCbs.current.delete(cb);
    },
    latestFrame: () => latest.current,
    probe,
    host: () => hostRef.current,
  };
}
